import type {
  WorkspaceSummaryResponse,
  WorkspaceThreadSummary,
} from "../../../shared/api-types";
import { rdSegmentOf, type RailModel, type RailRd } from "./rail-sections";

// Chats about an RD in the single-rail frame (NO-754): which chats the rail
// nests under an RD row, how many show before "Show N more", which row hosts
// them, and where the RD page's Chats card sits. Pure and relative-import
// only, so `tsx --test` runs it.

/** The rail shows this many chats per RD before "Show N more". */
export const RAIL_CHAT_LIMIT = 5;

/**
 * The row that hosts each RD's chats: the first RD row for it, the same row
 * `activeRailRow` marks, so an RD on the team and in Recently viewed nests its
 * chats once. Keyed by RD id.
 */
export function railChatHosts(model: RailModel): Map<string, string> {
  const hosts = new Map<string, string>();

  for (const row of model.sections.flatMap((section) => section.rows)) {
    if (row.kind === "rd" && !hosts.has(row.rd.id)) hosts.set(row.rd.id, row.key);
  }

  return hosts;
}

/** Is the URL anywhere under this RD (`/rds/<slug or id>/…`)? */
export function isOnRd(rd: RailRd, pathname: string): boolean {
  const segment = rdSegmentOf(pathname);

  return segment === rd.id || segment === rd.slug;
}

/**
 * The workspace-summary param the rail reads an RD's chats with. The RD you
 * are on uses the URL's own segment, so the rail shares the RD page's cache
 * entry; any other RD uses its slug, as the rail's links do.
 */
export function railSummaryParam(rd: RailRd, pathname: string): string {
  return isOnRd(rd, pathname) ? (rdSegmentOf(pathname) ?? rd.slug) : rd.slug;
}

/** The viewer's own chats about this RD, or null until the summary is this RD's. */
export function ownChatsFor(
  summary: WorkspaceSummaryResponse | undefined,
  rd: Pick<RailRd, "id">
): WorkspaceThreadSummary[] | null {
  return summary?.rdId === rd.id ? summary.threads : null;
}

/**
 * Is an RD row open? The RD you are on opens by itself; a chevron click is
 * remembered and wins, so an RD opened by hand stays open as you navigate.
 */
export function railRdOpen(current: boolean, choice: boolean | undefined): boolean {
  return choice ?? current;
}

/**
 * Does an RD row show a chevron? The rail cannot know an RD has chats until
 * its summary loads, so an unknown RD (`chatCount` null) keeps its chevron;
 * one known to have none loses it unless it is open or the viewer has already
 * opened or closed it (`chosen`). A chevron never vanishes under the click
 * that closed it, so a closed RD with no chats can always reopen to its
 * "+ New chat" row.
 */
export function railRdChevron({
  open,
  chosen,
  chatCount,
}: {
  open: boolean;
  chosen: boolean;
  chatCount: number | null;
}): boolean {
  return open || chosen || chatCount !== 0;
}

export type RailRdMark = "current" | "parent" | "none";

/**
 * How an RD row marks itself. On a chat whose row shows under it (a listed
 * thread, or New chat) the chat row is the one highlight and the RD row only
 * names where you are; with the chat's row out of sight (the RD closed, or the
 * thread past "Show N more") the RD row keeps the highlight.
 */
export function railRdMark({
  current,
  open,
  newChat,
  chatId,
  listedChatIds,
}: {
  current: boolean;
  open: boolean;
  newChat: boolean;
  chatId: string | null;
  listedChatIds: readonly string[];
}): RailRdMark {
  if (!current) return "none";

  const chatRowShown = newChat || (chatId !== null && listedChatIds.includes(chatId));

  return open && chatRowShown ? "parent" : "current";
}

function newestFirst(a: WorkspaceThreadSummary, b: WorkspaceThreadSummary): number {
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
}

export interface RailChatList {
  chats: WorkspaceThreadSummary[];
  /** How many more "Show N more" reveals; 0 once shown or when none are hidden. */
  hiddenCount: number;
}

/** Newest first, the first five unless the viewer asked for all. */
export function railChatList(
  threads: readonly WorkspaceThreadSummary[],
  showAll: boolean
): RailChatList {
  const sorted = [...threads].sort(newestFirst);
  if (showAll) return { chats: sorted, hiddenCount: 0 };

  return {
    chats: sorted.slice(0, RAIL_CHAT_LIMIT),
    hiddenCount: Math.max(sorted.length - RAIL_CHAT_LIMIT, 0),
  };
}

/** "Jess" for "Jess Romero"; the whole name when there is no first word. */
export function rdFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export type ChatsCardSurface = "overview" | "sessions";

/**
 * Where the RD page shows its Chats card. Overview exists only while
 * supervisor-metrics is on; without it `/rds/$rdId` lands on Sessions, so the
 * card goes there.
 */
export function chatsCardSurface(metricsEnabled: boolean): ChatsCardSurface {
  return metricsEnabled ? "overview" : "sessions";
}

/** Does this surface show the Chats card? Rail frame only; classic is unchanged. */
export function showsChatsCard({
  frame,
  metricsEnabled,
  surface,
}: {
  frame: "classic" | "rail";
  metricsEnabled: boolean;
  surface: ChatsCardSurface;
}): boolean {
  return frame === "rail" && chatsCardSurface(metricsEnabled) === surface;
}
